import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

/**
 * The single redirect URI every assistant provider sends the browser back to.
 *
 * It is a PUBLIC route by design: this page runs inside the OAuth popup, whose
 * MSAL cache is empty (in-memory cache, fresh window). It never talks to the
 * API. It hands `code` + `state` to the window that opened it — which is still
 * signed in — and closes. The opener POSTs them to the API with its bearer
 * token, so no unauthenticated endpoint exists for the callback.
 */
export const Route = createFileRoute('/assistant/oauth/callback')({
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
    error_description: typeof search.error_description === 'string' ? search.error_description : undefined,
    // Entra's admin-consent endpoint redirects here too, with no opener window.
    admin_consent: typeof search.admin_consent === 'string' ? search.admin_consent : undefined,
    tenant: typeof search.tenant === 'string' ? search.tenant : undefined,
  }),
  component: OAuthCallbackPage,
});

function OAuthCallbackPage() {
  const search = Route.useSearch();
  const [status, setStatus] = useState<'working' | 'done' | 'orphan' | 'consent'>('working');

  useEffect(() => {
    const opener = window.opener as Window | null;
    if (search.admin_consent !== undefined && (!opener || opener.closed)) {
      setStatus('consent');
      return;
    }
    if (!opener || opener.closed) {
      setStatus('orphan');
      return;
    }
    opener.postMessage(
      {
        type: 'assistant-oauth',
        code: search.code,
        state: search.state,
        error: search.error,
        errorDescription: search.error_description,
      },
      window.location.origin,
    );
    setStatus('done');
    const t = setTimeout(() => window.close(), 400);
    return () => clearTimeout(t);
  }, [search.code, search.state, search.error, search.error_description, search.admin_consent]);

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 24, background: 'var(--bg-page)' }}>
      <div className="card" style={{ maxWidth: 420, textAlign: 'center' }}>
        {status === 'working' && <p className="muted">Finishing sign-in…</p>}
        {status === 'done' && (
          <>
            <p>{search.error ? 'Sign-in was not completed.' : 'Connected.'}</p>
            <p className="muted">This window will close by itself.</p>
          </>
        )}
        {status === 'consent' && (
          <>
            <p>
              {search.admin_consent?.toLowerCase() === 'true'
                ? `Admin consent granted${search.tenant ? ` for tenant ${search.tenant}` : ''}.`
                : `Admin consent was not granted${search.error_description ? `: ${search.error_description}` : ''}.`}
            </p>
            <p className="muted">Close this window, then use Connect on the Connections page to sign in to a mailbox from that tenant.</p>
          </>
        )}
        {status === 'orphan' && (
          <>
            <p>This page only works inside the sign-in window opened from Connections.</p>
            <p className="muted">Close it and start the connection again from the app.</p>
          </>
        )}
      </div>
    </div>
  );
}
